import { AlertTriangle, ArrowLeft, Eye, Pause, Play, Search } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, ConfirmDialog, cx, ErrorNote, Spinner } from "../../components/ui";
import { api, useApi } from "../../lib/api";
import { useConnections } from "../../lib/connections";
import { passes, type Definition, type Prompt, type RerunMode, type RerunOption } from "./types";

/** <input type=date> wants YYYY-MM-DD; prompts and lookups use MM/DD/YYYY. */
const toInput = (v: string) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : v;
};
const fromInput = (v: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : v;
};
const LETTERS = ["0", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ", ..."123456789"];
const prefix = (d: string) => {
  const m = /^(\d{2})\/(\d{2})\/\d{4}$/.exec(d);
  return m ? `${LETTERS[Number(m[2])]}${LETTERS[Number(m[1])]}` : "";
};

type Option = { value: string; label: string; fills?: Record<string, string> };
interface Preview {
  facts: string[];
  problems: string[];
  writes: number;
  reports: number;
  runKey: string;
  values: Record<string, string>;
  steps: { stepId: string; seq: number; name: string; jobName?: string; runs: boolean; note?: string; breakAfter: boolean; rerun?: { field: string; mode: RerunMode; value?: boolean; why?: string }; send?: { method: string; path: string; params: Record<string, string> }; check: string; now?: string; reports: { name: string; requestName: string; archive?: string }[]; warnings: string[]; branch?: string; waitsFor?: string[] }[];
}

/** Run screen (design §8.6): choose the connection and the payroll's prompt values, then start. */
export default function JaRun() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const isDraft = search.get("draft") === "1";
  const saved = useApi<{ definition: Definition; reruns?: Record<string, RerunOption> }>(`/apps/ja/automations/${id}`);
  const draftDef = useApi<{ definition: Definition }>(isDraft ? `/apps/ja/automations/${id}/draft` : null);
  const data = isDraft ? (draftDef.data ? { definition: draftDef.data.definition, reruns: saved.data?.reruns } : null) : saved.data;
  const error = saved.error ?? draftDef.error;
  const reruns = data?.reruns ?? {};
  const [rerun, setRerun] = useState<Record<string, RerunMode>>({});
  useEffect(() => {
    if (data?.reruns) setRerun(Object.fromEntries(Object.entries(data.reruns).map(([k, o]) => [k, o.default])));
  }, [data?.reruns]);
  const { apps, reload: reloadConnections } = useConnections();
  useEffect(() => {
    void reloadConnections();
  }, [reloadConnections]);
  const conns = (apps.ja?.options ?? []).filter((o) => o.usable && !o.production);
  const [connId, setConnId] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [startAt, setStartAt] = useState("");
  const [debug, setDebug] = useState(false);
  const [lookups, setLookups] = useState<Record<string, { busy?: boolean; options?: Option[]; error?: string }>>({});
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [openStep, setOpenStep] = useState<string | null>(null);
  const runPreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      setPreview(await api<Preview>(`/apps/ja/automations/${id}/preview`, { body: { connId, values, startAt: startAt || undefined, rerun, ...(isDraft ? { draft: true } : {}) } }));
    } catch (e) {
      setPreviewError((e as Error).message);
    } finally {
      setPreviewing(false);
    }
  };
  const def = data?.definition;

  useEffect(() => {
    if (!connId && conns.length) setConnId(conns.find((c) => c.connId === apps.ja?.active)?.connId ?? conns[0].connId);
  }, [conns, connId, apps.ja?.active]);
  useEffect(() => {
    if (def) setValues(Object.fromEntries(def.prompts.map((p) => [p.code, p.default ?? ""])));
  }, [def]);

  const set = (code: string, v: string) => setValues((cur) => ({ ...cur, [code]: v }));
  const load = async (p: Prompt) => {
    setLookups((l) => ({ ...l, [p.code]: { busy: true } }));
    try {
      const r = await api<{ options: Option[] }>(`/apps/ja/automations/${id}/lookup`, { body: { prompt: p.code, connId, values } });
      setLookups((l) => ({ ...l, [p.code]: { options: r.options } }));
    } catch (e) {
      setLookups((l) => ({ ...l, [p.code]: { error: (e as Error).message } }));
    }
  };
  const choose = (p: Prompt, v: string) => {
    set(p.code, v);
    const opt = lookups[p.code]?.options?.find((o) => o.value === v);
    for (const [k, val] of Object.entries(opt?.fills ?? {})) set(k, val);
  };

  const plan = useMemo(() => {
    if (!def) return [];
    let n = 0;
    const startIdx = startAt ? def.trunk.findIndex((s) => s.id === startAt) : 0;
    return def.trunk.map((s, i) => {
      const count = passes(def, s, values);
      const applies = (!s.when || (values[s.when.prompt] ?? "") === s.when.equals) && count > 0;
      const p = prefix(values[def.runDatePrompt] ?? "");
      const first = n + 1;
      if (applies) n += count;
      return { s, applies, count, job: !applies ? "" : count > 1 ? `${p}${first}–${n}` : `${p}${n}`, skipped: applies && i < startIdx };
    });
  }, [def, values, startAt]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!def) return <Spinner />;
  const missing = def.prompts.filter((p) => p.required && !p.setBy && !(values[p.code] ?? "").trim()).map((p) => p.label);
  const conn = conns.find((c) => c.connId === connId);
  const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";

  const start = async () => {
    const r = await api<{ run: { runId: string } }>("/apps/ja/runs", { body: { automationId: id, connId, values, startAt: startAt || undefined, rerun, ...(isDraft ? { draft: true } : {}), ...(debug ? { debug: true } : {}) } });
    navigate(`/apps/ja/runs/${r.run.runId}`);
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <Link to="/apps/ja" className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline mb-3">
          <ArrowLeft className="size-4" aria-hidden /> Automations
        </Link>
        <h1 className="text-2xl font-bold text-ink">
          {isDraft ? "Test-run" : "Run"} {def.name} <span className="text-muted font-normal text-base">· {def.type} · {isDraft ? "working draft" : `v${def.version}`}</span>
        </h1>
        <p className="text-sm text-muted">Choose this payroll's values. Each step sends them to Infor, and the run pauses at each break for you to review.</p>
      </div>

      <div className="grid md:grid-cols-[1fr_1fr] gap-6">
        <section className="rounded-xl border border-line bg-surface p-4 space-y-4">
          <label className="block text-sm">
            <span className="text-ink-2 font-medium">Infor connection</span>
            {conns.length ? (
              <select className={input} value={connId} onChange={(e) => setConnId(e.target.value)}>
                {conns.map((c) => (
                  <option key={c.connId} value={c.connId}>
                    {c.name} ({c.envLabel})
                  </option>
                ))}
              </select>
            ) : (
              <span className="block text-bad text-sm">
                No usable non-production connection.
                {(apps.ja?.options ?? []).map((o) => (
                  <span key={o.connId} className="block text-xs">
                    {o.name}: {o.production ? "production connections can't run Job Automation yet" : o.reason ?? "not usable"}
                  </span>
                ))}
              </span>
            )}
          </label>
          {def.prompts.map((p) => {
            const lk = lookups[p.code];
            const isKey = def.runKey.includes(p.code);
            return (
              <div key={p.code} className="text-sm">
                <label className="block">
                  <span className="text-ink-2 font-medium">
                    {p.label}
                    {p.required && !p.setBy && <span className="text-bad"> *</span>}
                    {isKey && <span className="text-xs text-muted font-normal"> · run key</span>}
                  </span>
                  {p.kind === "select" ? (
                    <select className={input} value={values[p.code] ?? ""} onChange={(e) => set(p.code, e.target.value)}>
                      {/* A required choice with no default starts empty, so what's shown is what will be sent. */}
                      {(!p.required || !p.default) && <option value="">{p.required ? "Choose…" : "—"}</option>}
                      {p.options?.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label === o.value ? o.value : `${o.value} – ${o.label}`}
                        </option>
                      ))}
                    </select>
                  ) : lk?.options ? (
                    <select className={input} value={values[p.code] ?? ""} onChange={(e) => choose(p, e.target.value)}>
                      <option value="">Choose…</option>
                      {lk.options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label && o.label !== o.value ? `${o.value} – ${o.label}` : o.value}
                        </option>
                      ))}
                    </select>
                  ) : p.kind === "date" ? (
                    <input type="date" className={input} value={toInput(values[p.code] ?? "")} onChange={(e) => set(p.code, fromInput(e.target.value))} />
                  ) : (
                    <input className={input} value={values[p.code] ?? ""} maxLength={p.maxLength} onChange={(e) => set(p.code, e.target.value)} />
                  )}
                </label>
                {p.lookup && (
                  <button type="button" className="mt-1 inline-flex items-center gap-1 text-xs text-accent-text hover:underline disabled:opacity-50" disabled={!connId || lk?.busy} title={!connId ? "Choose an Infor connection first" : undefined} onClick={() => void load(p)}>
                    <Search className="size-3" aria-hidden /> {lk?.busy ? "Looking up…" : lk?.options ? `Look up again (${lk.options.length})` : "Look up in Infor"}
                  </button>
                )}
                {lk?.error && <span className="block text-xs text-bad">{lk.error}</span>}
                {(p.code === "checknumber" || p.code === "receiptnumber") && <span className="block text-xs text-muted">Filled from the bank code just before printing.</span>}
              </div>
            );
          })}
        </section>

        <section className="space-y-3">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={debug} onChange={(e) => setDebug(e.target.checked)} />
            <span>
              <span className="text-ink-2 font-medium">Debug mode</span>
              <span className="block text-xs text-muted">Pause at each debugger line in the automation's functions and show what the function sees. Nothing is sent for the step until you continue.</span>
            </span>
          </label>
          <label className="block text-sm">
            <span className="text-ink-2 font-medium">Start at</span>
            <select className={input} value={startAt} onChange={(e) => setStartAt(e.target.value)}>
              <option value="">The first step</option>
              {def.trunk.map((s, i) => (
                <option key={s.id} value={s.id}>
                  {i + 1}. {s.name}
                </option>
              ))}
            </select>
          </label>
          <div className="rounded-xl border border-line bg-surface">
            <div className="px-3 py-2 border-b border-line text-sm font-medium text-ink">What this run will do</div>
            <ol className="text-sm divide-y divide-line">
              {plan.map(({ s, applies, count, job, skipped }) => (
                <li key={s.id} className={cx("px-3 py-1.5 flex items-center gap-2", (!applies || skipped) && "text-muted")}>
                  <span className={cx("font-mono text-xs shrink-0", count > 1 ? "w-auto min-w-10" : "w-10")}>{job}</span>
                  <span className={cx("flex-1", (!applies || skipped) && "line-through")}>{s.name}</span>
                  {!applies && (count === 0 ? <span className="text-xs">no rows of {s.repeat!.array} match</span> : <span className="text-xs">needs {s.when!.prompt} = {s.when!.equals}</span>)}
                  {applies && s.repeat && <span className="text-xs text-ink-2">× {count} rows of {s.repeat.array}</span>}
                  {applies && s.loop && <span className="text-xs text-ink-2">loop, up to {count}</span>}
                  {skipped && <span className="text-xs">before start</span>}
                  {applies && !skipped && reruns[s.id] && (
                    <label
                      className="inline-flex items-center gap-1 text-xs text-ink-2"
                      title={`Auto: rerun only if ${reruns[s.id].flag ?? "the step"} shows it ran before this cycle (read just before sending). Yes/No: always send ${reruns[s.id].field} = true/false.${reruns[s.id].required ? " Infor refuses a second run without it." : ""}`}
                    >
                      Rerun
                      <select className="h-6 rounded border border-line bg-surface text-xs" value={rerun[s.id] ?? reruns[s.id].default} onChange={(e) => setRerun((r) => ({ ...r, [s.id]: e.target.value as RerunMode }))} onClick={(e) => e.stopPropagation()}>
                        {reruns[s.id].flag && <option value="auto">Auto</option>}
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                  )}
                  {applies && !skipped && s.breakAfter && <Pause className="size-3.5 text-warn" aria-label="Pauses for review" />}
                </li>
              ))}
            </ol>
          </div>
          {missing.length > 0 && <p className="text-xs text-muted">Still needed: {missing.join(", ")}</p>}
          <div className="flex flex-wrap gap-2">
            <Button disabled={!conn || missing.length > 0} busy={previewing} onClick={() => void runPreview()}>
              <Eye className="size-4" aria-hidden /> Preview (reads only)
            </Button>
            <Button variant="primary" disabled={!conn || missing.length > 0} onClick={() => setConfirming(true)}>
              <Play className="size-4" aria-hidden /> Start run
            </Button>
          </div>
        </section>
      </div>

      {previewError && <ErrorNote>{previewError}</ErrorNote>}
      {preview && (
        <section className="space-y-3">
          <h2 className="font-display font-semibold text-ink">
            Preview <span className="text-sm font-normal text-muted">· nothing was sent to Infor · {preview.writes} requests and {preview.reports} reports would be sent</span>
          </h2>
          {preview.problems.length > 0 && (
            <div className="rounded-xl border border-warn bg-warn-soft p-3 text-sm space-y-1">
              {preview.problems.map((p) => (
                <div key={p} className="flex gap-2 text-ink">
                  <AlertTriangle className="size-4 text-warn mt-0.5 shrink-0" aria-hidden /> {p}
                </div>
              ))}
            </div>
          )}
          {preview.facts.map((f) => (
            <p key={f} className="text-xs text-muted">
              {f}
            </p>
          ))}
          <div className="overflow-x-auto rounded-xl border border-line bg-surface">
            <table className="w-full text-sm">
              <thead className="text-left text-muted border-b border-line">
                <tr>
                  <th className="px-3 py-2 font-medium">Job</th>
                  <th className="px-3 py-2 font-medium">Step</th>
                  <th className="px-3 py-2 font-medium">Would send</th>
                  <th className="px-3 py-2 font-medium">Checked by</th>
                  <th className="px-3 py-2 font-medium">Infor now</th>
                </tr>
              </thead>
              <tbody>
                {preview.steps.map((s) => (
                  <Fragment key={s.stepId}>
                    <tr className={cx("border-b border-line align-top cursor-pointer hover:bg-surface-2", !s.runs && "text-muted")} onClick={() => setOpenStep(openStep === s.stepId ? null : s.stepId)}>
                      <td className="px-3 py-2 font-mono text-xs">{s.jobName}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium flex items-center gap-1.5">
                          {s.name}
                          {s.breakAfter && s.runs && <Pause className="size-3.5 text-warn" aria-label="Pauses for review" />}
                          {s.runs && s.rerun && (
                            <span className={cx("text-xs", s.rerun.value ? "font-semibold text-accent-text" : "text-muted")} title={s.rerun.why}>
                              {s.rerun.value ? "Rerun" : "Not a rerun"}
                              {s.rerun.mode === "auto" ? " (Auto)" : ""}
                            </span>
                          )}
                        </div>
                        {s.branch && <div className="text-xs text-accent-text">↳ in branch {s.branch} (runs alongside)</div>}
                        {s.waitsFor?.length ? <div className="text-xs text-accent-text">Waits for {s.waitsFor.join(", ")}</div> : null}
                        {s.note && <div className="text-xs">{s.note}</div>}
                        {s.warnings.map((w) => (
                          <div key={w} className="text-xs text-warn">
                            ⚠ {w}
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs break-all">{s.send ? `${s.send.method} ${s.send.path}` : "—"}</td>
                      <td className="px-3 py-2 text-xs text-ink-2">{s.runs ? s.check : ""}</td>
                      <td className="px-3 py-2 text-xs text-ink-2">
                        {s.now}
                        {s.reports.map((r) => (
                          <div key={r.name} className="text-muted">
                            Report “{r.requestName}”{r.archive && ` → ${r.archive}`}
                          </div>
                        ))}
                      </td>
                    </tr>
                    {openStep === s.stepId && s.send && (
                      <tr className="border-b border-line bg-surface-2">
                        <td />
                        <td colSpan={4} className="px-3 py-3 text-xs">
                          <table className="w-full">
                            <tbody>
                              {Object.entries(s.send.params).map(([k, v]) => (
                                <tr key={k} className="align-top">
                                  <td className="pr-4 py-0.5 font-mono whitespace-nowrap">{k}</td>
                                  <td className="py-0.5 text-ink-2 break-all font-mono">{v === "" ? <span className="text-muted">(blank)</span> : v.length > 600 ? `${v.slice(0, 600)}…` : v}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted">Select a step to see every field it would send. Check numbers are read live and may move on before the run.</p>
        </section>
      )}

      {confirming && conn && (
        <ConfirmDialog title={`Start ${def.type} on ${conn.name}?`} confirm="Start" onConfirm={start} onClose={() => setConfirming(false)}>
          <p className="text-sm text-ink-2">
            This sends real requests to <strong>{conn.name}</strong> ({conn.envLabel}), starting with {plan.find((x) => x.applies && !x.skipped)?.s.name}. It pauses at each break for you to review before going on.
          </p>
          <p className="text-sm text-muted mt-2">{def.runKey.map((k) => `${def.prompts.find((p) => p.code === k)?.label}: ${values[k]}`).join(" · ")}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
